import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { SideDrawer } from "@/components/common/SideDrawer/SideDrawer";
import { useUnlinkedInvocations } from "@/hooks/brain/useUnlinkedInvocations";
import { SessionService } from "@/services/brain/SessionService";
import { CopyableJsonBlock } from "./CopyableJsonBlock";

interface UnlinkedInvocationsDrawerProps {
  userId: string | null;
  onClose: () => void;
}

export const UnlinkedInvocationsDrawer = ({ userId, onClose }: UnlinkedInvocationsDrawerProps) => {
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useUnlinkedInvocations(userId);
  const invocations = data?.invocations ?? [];

  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [confirmDeleteAll, setConfirmDeleteAll] = useState(false);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["brain", "unlinked-invocations", userId] });
    queryClient.invalidateQueries({ queryKey: ["brain", "sessions", userId] });
  };

  const deleteOneMutation = useMutation({
    mutationFn: (invocationId: string) => SessionService.deleteUnlinkedInvocation(invocationId, userId as string),
    onSuccess: () => {
      toast.success("Invocation deleted");
      invalidate();
    },
    onError: () => toast.error("Failed to delete invocation"),
    onSettled: () => setPendingDeleteId(null),
  });

  const deleteAllMutation = useMutation({
    mutationFn: () => SessionService.deleteAllUnlinkedInvocations(userId as string),
    onSuccess: (result) => {
      toast.success(`Deleted ${result.deleted} unlinked invocation${result.deleted !== 1 ? "s" : ""}`);
      invalidate();
    },
    onError: () => toast.error("Failed to delete unlinked invocations"),
    onSettled: () => setConfirmDeleteAll(false),
  });

  return (
    <SideDrawer isOpen={!!userId} onClose={onClose} width="55vw" title="Unlinked Tool Invocations">
      <div className="py-4 space-y-4">
        <div className="flex items-center justify-between gap-4">
          <p className="text-xs text-genie-text-muted">
            These invocations predate session tracking, or errored before their session could be recorded — see
            the migration notes for known gaps.
          </p>
          {invocations.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              className="gap-2 text-genie-error border-genie-error/30 hover:bg-genie-error-light hover:text-genie-error flex-shrink-0"
              onClick={() => setConfirmDeleteAll(true)}
            >
              <Trash2 className="h-3.5 w-3.5" />
              Delete all
            </Button>
          )}
        </div>
        {isLoading ? (
          <div className="text-center py-12 text-genie-text-secondary">
            <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2 text-genie-primary" />
            Loading...
          </div>
        ) : isError ? (
          <div className="text-center py-12 text-genie-text-muted">Failed to load unlinked invocations</div>
        ) : invocations.length === 0 ? (
          <div className="text-center py-12 text-genie-text-muted">No unlinked invocations found</div>
        ) : (
          invocations.map((invocation) => (
            <div key={invocation.id} className="border border-genie-border rounded-lg p-3 space-y-2">
              <div className="flex items-center justify-between gap-2 text-xs text-genie-text-secondary">
                <span className="font-medium text-genie-text-primary">{invocation.tool_name}</span>
                <div className="flex items-center gap-2">
                  <span>{new Date(invocation.created_at).toLocaleString()}</span>
                  <button
                    onClick={() => setPendingDeleteId(invocation.id)}
                    className="p-1 text-genie-text-muted hover:text-genie-error hover:bg-genie-error-light rounded transition-colors"
                    aria-label="Delete invocation"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <CopyableJsonBlock label="Request" value={invocation.request} />
                {invocation.response && <CopyableJsonBlock label="Response" value={invocation.response} />}
              </div>
              {invocation.error && <CopyableJsonBlock label="Error" value={invocation.error} tone="error" />}
            </div>
          ))
        )}
      </div>

      <AlertDialog open={!!pendingDeleteId} onOpenChange={(open) => !open && setPendingDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this invocation?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the tool invocation record. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => pendingDeleteId && deleteOneMutation.mutate(pendingDeleteId)}
              disabled={deleteOneMutation.isPending}
            >
              {deleteOneMutation.isPending && <RefreshCw className="h-4 w-4 animate-spin mr-2" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmDeleteAll} onOpenChange={setConfirmDeleteAll}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete all unlinked invocations?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes every unlinked tool invocation for this user, including any beyond the{" "}
              {invocations.length} shown here. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => deleteAllMutation.mutate()} disabled={deleteAllMutation.isPending}>
              {deleteAllMutation.isPending && <RefreshCw className="h-4 w-4 animate-spin mr-2" />}
              Delete all
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SideDrawer>
  );
};
